import React, { useEffect, useState, cloneElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UsersIcon, BookIcon, HeartIcon, FlameIcon, HeartHandshakeIcon, ListIcon, AlertCircleIcon, BookOpenIcon, MapPinIcon, UserIcon, ZapIcon, BookmarkIcon, LinkIcon, GlobeIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
type CategoryKey = 'all' | 'romance' | 'fantasy' | 'mystery' | 'scifi' | 'horror' | 'general';
interface Category {
  key: CategoryKey;
  label: string;
  icon: JSX.Element;
  color: string;
}
interface RelationshipType {
  id: string;
  name: string;
  category: CategoryKey;
  count: number;
  popular: boolean;
  description?: string;
}
const RelationshipTypes: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<'popularity' | 'alphabetical' | 'bookCount' | 'viewAll'>('popularity');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  useEffect(() => {
    const category = searchParams.get('category');
    setSelectedCategory(category as CategoryKey || 'all');
  }, [searchParams]);
  const categories: Category[] = [{
    key: 'all',
    label: 'All Relationship Types',
    icon: <UsersIcon className="h-4 w-4" data-id="element-13249" data-mp="2jnx6a6rrc34f" />,
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    key: 'romance',
    label: 'Romance',
    icon: <HeartIcon className="h-4 w-4" data-id="element-13250" data-mp="2943e85hooua4" />,
    color: 'bg-pink-100 text-pink-800'
  }, {
    key: 'fantasy',
    label: 'Fantasy',
    icon: <BookIcon className="h-4 w-4" data-id="element-13251" data-mp="2023z5iq8c09t" />,
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    key: 'mystery',
    label: 'Mystery & Thriller',
    icon: <BookIcon className="h-4 w-4" data-id="element-13252" data-mp="2spq5gyqudj0d" />,
    color: 'bg-amber-100 text-amber-800'
  }, {
    key: 'scifi',
    label: 'Sci-Fi',
    icon: <BookIcon className="h-4 w-4" data-id="element-13253" data-mp="2wq1mm1jzx173" />,
    color: 'bg-cyan-100 text-cyan-800'
  }, {
    key: 'horror',
    label: 'Horror',
    icon: <BookIcon className="h-4 w-4" data-id="element-13254" data-mp="2nz1b9bns27xj" />,
    color: 'bg-red-100 text-red-800'
  }, {
    key: 'general',
    label: 'General',
    icon: <BookIcon className="h-4 w-4" data-id="element-13255" data-mp="3bnkidnv3ceo1" />,
    color: 'bg-gray-100 text-gray-800'
  }];
  const relationshipTypes: RelationshipType[] = [
  // Romance
  {
    id: '1',
    name: 'Enemies to Lovers',
    category: 'romance',
    count: 312,
    popular: true,
    description: 'Characters who start as adversaries and develop romantic feelings over time.'
  }, {
    id: '2',
    name: 'Friends to Lovers',
    category: 'romance',
    count: 278,
    popular: true,
    description: 'A friendship that slowly evolves into a romantic relationship.'
  }, {
    id: '3',
    name: 'Forbidden Love',
    category: 'romance',
    count: 245,
    popular: true,
    description: 'A romance that crosses social, familial, or moral boundaries.'
  }, {
    id: '4',
    name: 'Second Chance',
    category: 'romance',
    count: 221,
    popular: true,
    description: 'Former lovers reunited, navigating old wounds and new feelings.'
  }, {
    id: '5',
    name: 'Fake Dating',
    category: 'romance',
    count: 198,
    popular: true,
    description: 'Characters pretend to be a couple but develop real feelings.'
  }, {
    id: '6',
    name: 'Rivals to Lovers',
    category: 'romance',
    count: 187,
    popular: true,
    description: 'Competing characters who discover their rivalry masks deeper feelings.'
  }, {
    id: '7',
    name: 'Student / Teacher',
    category: 'romance',
    count: 134,
    popular: true,
    description: 'A romance between a student and their instructor or mentor figure.'
  }, {
    id: '8',
    name: 'Boss / Employee',
    category: 'romance',
    count: 156,
    popular: true,
    description: 'Workplace romance with a power dynamic between employer and employee.'
  }, {
    id: '9',
    name: 'Age Gap',
    category: 'romance',
    count: 143,
    popular: true,
    description: 'A significant age difference shapes the dynamic between the couple.'
  }, {
    id: '10',
    name: 'Childhood Sweethearts',
    category: 'romance',
    count: 112,
    popular: true,
    description: 'Characters who knew each other as children and reconnect as adults.'
  }, {
    id: '11',
    name: 'Protector / Protected',
    category: 'romance',
    count: 167,
    popular: true,
    description: 'One character is tasked with or compelled to protect the other.'
  }, {
    id: '12',
    name: 'Opposites Attract',
    category: 'romance',
    count: 189,
    popular: true,
    description: 'Characters with contrasting personalities who are drawn together.'
  },
  // Fantasy
  {
    id: '13',
    name: 'Chosen One & Mentor',
    category: 'fantasy',
    count: 198,
    popular: true,
    description: 'A destined hero guided by a wise or powerful mentor figure.'
  }, {
    id: '14',
    name: 'Rivals',
    category: 'fantasy',
    count: 176,
    popular: true,
    description: 'Characters competing for power, status, or a shared goal.'
  }, {
    id: '15',
    name: 'Forbidden (Cross-Species)',
    category: 'fantasy',
    count: 154,
    popular: true,
    description: 'Love or alliance between characters from different species or magical races.'
  }, {
    id: '16',
    name: 'Forbidden (Class / Rank)',
    category: 'fantasy',
    count: 143,
    popular: true,
    description: 'A bond that crosses rigid social hierarchies or noble bloodlines.'
  }, {
    id: '17',
    name: 'Found Family',
    category: 'fantasy',
    count: 234,
    popular: true,
    description: 'A group of unrelated characters who form deep familial bonds through shared experience.'
  }, {
    id: '18',
    name: 'Fated Mates',
    category: 'fantasy',
    count: 212,
    popular: true,
    description: 'Characters destined by magic, prophecy, or supernatural bond to be together.'
  }, {
    id: '19',
    name: 'Captor & Captive',
    category: 'fantasy',
    count: 132,
    popular: true,
    description: 'One character holds power over another, with tension that evolves over the story.'
  }, {
    id: '20',
    name: 'Sworn Enemies',
    category: 'fantasy',
    count: 145,
    popular: true,
    description: 'Characters from opposing factions or bloodlines forced into proximity.'
  },
  // Mystery & Thriller
  {
    id: '21',
    name: 'Detective & Suspect',
    category: 'mystery',
    count: 189,
    popular: true,
    description: 'An investigator and someone under suspicion — trust is always in question.'
  }, {
    id: '22',
    name: 'Partners',
    category: 'mystery',
    count: 167,
    popular: true,
    description: 'Two investigators or operatives working together, often with friction.'
  }, {
    id: '23',
    name: 'Unlikely Allies',
    category: 'mystery',
    count: 143,
    popular: true,
    description: 'Characters from opposing sides forced to cooperate toward a shared goal.'
  }, {
    id: '24',
    name: 'Predator & Prey',
    category: 'mystery',
    count: 121,
    popular: true,
    description: 'A dangerous cat-and-mouse dynamic where one pursues and one evades.'
  }, {
    id: '25',
    name: 'Witness & Protector',
    category: 'mystery',
    count: 98,
    popular: false,
    description: 'A witness in danger placed under the protection of a law enforcement figure.'
  },
  // Sci-Fi
  {
    id: '26',
    name: 'Human & AI',
    category: 'scifi',
    count: 156,
    popular: true,
    description: 'A bond — romantic or otherwise — between a human and an artificial intelligence.'
  }, {
    id: '27',
    name: 'Alien & Human',
    category: 'scifi',
    count: 134,
    popular: true,
    description: 'Cross-species connection between humans and extraterrestrial beings.'
  }, {
    id: '28',
    name: 'Crew / Found Family',
    category: 'scifi',
    count: 178,
    popular: true,
    description: "A spaceship crew or team who become each other's family across the stars."
  }, {
    id: '29',
    name: 'Rivals (Factions)',
    category: 'scifi',
    count: 112,
    popular: true,
    description: 'Characters from opposing political or military factions drawn together.'
  }, {
    id: '30',
    name: 'Clone & Original',
    category: 'scifi',
    count: 87,
    popular: false,
    description: 'A complex dynamic between a cloned individual and their original counterpart.'
  },
  // Horror
  {
    id: '31',
    name: 'Survivor & Monster',
    category: 'horror',
    count: 134,
    popular: true,
    description: 'A human survivor navigating a relationship — fear, fascination, or love — with a monster.'
  }, {
    id: '32',
    name: 'Haunted & Haunter',
    category: 'horror',
    count: 112,
    popular: true,
    description: 'A character haunted by a supernatural entity, with a bond that defies explanation.'
  }, {
    id: '33',
    name: 'Unlikely Allies (Horror)',
    category: 'horror',
    count: 98,
    popular: true,
    description: 'Characters who would never choose each other, united by survival.'
  }, {
    id: '34',
    name: 'Obsessive / Obsessed',
    category: 'horror',
    count: 123,
    popular: true,
    description: 'A dark, consuming fixation that drives the relationship — and the plot.'
  },
  // General
  {
    id: '35',
    name: 'Found Family',
    category: 'general',
    count: 267,
    popular: true,
    description: "Unrelated characters who become each other's family through shared struggle."
  }, {
    id: '36',
    name: 'Mentor / Protégé',
    category: 'general',
    count: 198,
    popular: true,
    description: 'An experienced guide shapes the growth of a less experienced character.'
  }, {
    id: '37',
    name: 'Rivals',
    category: 'general',
    count: 176,
    popular: true,
    description: 'Characters in direct competition — may or may not resolve into alliance.'
  }, {
    id: '38',
    name: 'Opposites',
    category: 'general',
    count: 154,
    popular: true,
    description: 'Characters with contrasting personalities, backgrounds, or worldviews.'
  }, {
    id: '39',
    name: 'Unlikely Friends',
    category: 'general',
    count: 143,
    popular: true,
    description: 'An unexpected friendship between characters who seem incompatible.'
  }, {
    id: '40',
    name: 'Chosen Family',
    category: 'general',
    count: 132,
    popular: true,
    description: 'Characters who deliberately choose each other as family despite no blood ties.'
  }];
  const filteredTypes = relationshipTypes.filter(rt => {
    if (selectedCategory !== 'all' && rt.category !== selectedCategory) return false;
    if (searchQuery && !rt.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const sortedTypes = [...filteredTypes].sort((a, b) => {
    if (sortOption === 'alphabetical') return a.name.localeCompare(b.name);
    if (sortOption === 'bookCount') return b.count - a.count;
    if (sortOption === 'viewAll') return a.name.localeCompare(b.name);
    if (a.popular !== b.popular) return a.popular ? -1 : 1;
    return b.count - a.count;
  });
  const grouped: Record<CategoryKey, RelationshipType[]> = {
    romance: [],
    fantasy: [],
    mystery: [],
    scifi: [],
    horror: [],
    general: [],
    all: []
  };
  sortedTypes.forEach(rt => {
    grouped[rt.category].push(rt);
  });
  const getColor = (category: CategoryKey): string => {
    if (darkMode) {
      switch (category) {
        case 'romance':
          return 'bg-pink-900/30 text-pink-300';
        case 'fantasy':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'mystery':
          return 'bg-amber-900/30 text-amber-300';
        case 'scifi':
          return 'bg-cyan-900/30 text-cyan-300';
        case 'horror':
          return 'bg-red-900/30 text-red-300';
        case 'general':
          return 'bg-gray-700 text-gray-300';
        default:
          return dk.badgeNeutral;
      }
    }
    return categories.find(c => c.key === category)?.color || 'bg-gray-100 text-gray-800';
  };
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Relationship Types'
    }, {
      value: 'romance',
      label: 'Romance'
    }, {
      value: 'fantasy',
      label: 'Fantasy'
    }, {
      value: 'mystery',
      label: 'Mystery & Thriller'
    }, {
      value: 'scifi',
      label: 'Sci-Fi'
    }, {
      value: 'horror',
      label: 'Horror'
    }, {
      value: 'general',
      label: 'General'
    }],
    value: selectedCategory,
    onChange: (val: string) => setSelectedCategory(val as CategoryKey)
  }, {
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'popularity',
      label: 'Popularity'
    }, {
      value: 'alphabetical',
      label: 'Alphabetical'
    }, {
      value: 'bookCount',
      label: 'Book Count'
    }, {
      value: 'viewAll',
      label: 'View All (flat list)'
    }],
    value: sortOption,
    onChange: (val: string) => setSortOption(val as typeof sortOption)
  }];
  const handleReset = () => {
    setSelectedCategory('all');
    setSortOption('popularity');
    setSearchQuery('');
  };
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    isActive: true,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/relationship-types?category=scifi'
    }, {
      label: 'Horror',
      href: '/relationship-types?category=horror'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    subItems: [{
      label: 'Sexual Orientation & Gender',
      href: '/representation?category=Sexual+Orientation+%26+Gender'
    }, {
      label: 'Race & Ethnicity',
      href: '/representation?category=Race+%26+Ethnicity'
    }, {
      label: 'Neurodiversity & Disability',
      href: '/representation?category=Neurodiversity+%26+Disability'
    }, {
      label: 'Mental Health',
      href: '/representation?category=Mental+Health'
    }]
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    subItems: [{
      label: 'Personal Growth',
      href: '/themes?category=Personal+Growth'
    }, {
      label: 'Destiny',
      href: '/themes?category=Destiny'
    }, {
      label: 'Power',
      href: '/themes?category=Power'
    }, {
      label: 'Morality',
      href: '/themes?category=Morality'
    }, {
      label: 'Relationships',
      href: '/themes?category=Relationships'
    }, {
      label: 'Identity',
      href: '/themes?category=Identity'
    }]
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  const renderTypes = () => {
    if (sortOption === 'viewAll' || selectedCategory !== 'all' || searchQuery) {
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-13256" data-mp="8jixxqv40hmp">
          {sortedTypes.map(rt => <Link key={rt.id} to={`/tagged-books/${rt.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`} className={`${getColor(rt.category)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-13257" data-mp="4f1gvzg4wifm">
              <div className="flex justify-between items-center mb-1" data-id="element-13258" data-mp="n7m4b1d2dvc1">
                <div className="font-medium" data-id="element-13259" data-mp="n9q69drekk9o">{rt.name}</div>
                <div className="text-xs opacity-80" data-id="element-13260" data-mp="116c1egzvvv0e">{rt.count} books</div>
              </div>
              {rt.description && <p className="text-xs mt-1 opacity-90" data-id="element-13261" data-mp="w93gw9r9jevz">{rt.description}</p>}
            </Link>)}
        </div>;
    }
    const labelMap: Record<string, string> = {};
    categories.forEach(c => {
      labelMap[c.key] = c.label;
    });
    const sortedEntries = sortEntriesAlphabetically(Object.entries(grouped).filter(([key]) => key !== 'all'), labelMap);
    return <div className="space-y-3" data-id="element-13262" data-mp="16xpv0g5e7e60">
        {sortedEntries.map(([category, types]) => {
        const categoryObj = categories.find(c => c.key === category);
        return <ContentAccordionSection key={category} title={categoryObj?.label || category} icon={cloneElement(categoryObj?.icon || <UsersIcon className="h-5 w-5" data-id="element-13264" data-mp="1pt6h58lzvjrx" />, {
          className: 'h-5 w-5'
        })} itemCount={types.length} data-id="element-13263" data-mp="1fxmmepj62r4r">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-13265" data-mp="1zj97f11qejmk">
                {types.map(rt => <Link key={rt.id} to={`/tagged-books/${rt.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`} className={`${getColor(rt.category as CategoryKey)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-13266" data-mp="23jt6ia5v7xbc">
                    <div className="flex justify-between items-center mb-1" data-id="element-13267" data-mp="3k2lehypccqne">
                      <div className="font-medium" data-id="element-13268" data-mp="3f4jv1co06pbe">{rt.name}</div>
                      <div className="text-xs opacity-80" data-id="element-13269" data-mp="fnv95t244n18">{rt.count} books</div>
                    </div>
                    {rt.description && <p className="text-xs mt-1 opacity-90" data-id="element-13270" data-mp="bn1nz7g21mc1">
                        {rt.description}
                      </p>}
                  </Link>)}
              </div>
            </ContentAccordionSection>;
      })}
      </div>;
  };
  return <div className={dk.page} data-id="element-13271" data-mp="zdnjjz8vhe55">
      <ContentPageHeader title="Relationship Types" icon={getContentPageIcon('relationshiptypes')} searchPlaceholder="Search relationship types..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-13272" data-mp="zdx8rkm3fh9s" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13273" data-mp="55q2xzckkklf">
        <div className="flex gap-8" data-id="element-13274" data-mp="57jdvqu7tqz8">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-13275" data-mp="a3hxz28igmtu">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={filterSections} onReset={handleReset} searchable={false} data-id="element-13276" data-mp="11xjx7abtff95" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-13277" data-mp="1gsgh4kszlr22">
            {sortedTypes.length === 0 ? <div className="text-center py-12" data-id="element-13278" data-mp="1ljyc8s8bwxf4">
                <p className={dk.textMuted} data-id="element-13279" data-mp="1lljaartuqyhq">
                  No relationship types found matching your criteria.
                </p>
              </div> : renderTypes()}
          </main>
        </div>
      </div>
    </div>;
};
export default RelationshipTypes;